import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import {
	AfasResourceKind,
	AfasResultStatus,
	MyTasksListModel,
	TaskListItem,
	getMyTasks
} from "client/Api/Workspace/LiveInformation/Afas/afasLiveInformation";
import {
	BusyIndicator,
	Button,
	ChangeTriggerKind,
	DateString
} from "client/Components";
import { DestinationWindow } from "client/Api/Apps/common";
import { AfasLiveInformationProvider } from "client/Features/Workspace/Tiles/Afas/provider";
import { Guid } from "client_legacy/ndaw/guid";
import { LiveTileContent } from "../LiveTileContent";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { localize } from "client_legacy/ndaw/culture";
import { useAfasLiveInformation } from "./hooks";
import { ListItem } from "../ListItem";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { isNil, isNotNil } from "client/Misc/utils";
import { OtpDialog } from "./OtpDialog";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { AlertManager, useAlerts } from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import styles from "./MyTasks.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { DimensionToRender } from "../../RenderState/common";

const PAGE_SIZE: number = 10;

export type MyTasksProps = {
	dimensionToRender: DimensionToRender;
	destinationWindow: DestinationWindow;
	provider: AfasLiveInformationProvider;
	tabId: Guid;
	connectionId: number;
	timezone: TimeZone;
	onListItemClick(): void;
};

export const MyTasks = (props: MyTasksProps): ReactElement => {
	const {
		dimensionToRender,
		destinationWindow,
		provider,
		tabId,
		connectionId,
		timezone,
		onListItemClick
	} = props;

	const [didUserManuallyLoadMore, setDidUserManuallyLoadMore] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [isOtpDialogOpen, setIsOtpDialogOpen] = useState<boolean>(false);

	const loadMoreAndSearchAbortControllerRef: RefObject<AbortController | null>
		= useRef<AbortController>(null);

	const alertManager: AlertManager = useAlerts();

	const {
		loadingState,
		setLoadingState,
		reload: refreshHandler
	} = useAfasLiveInformation(
		provider,
		tabId,
		AfasResourceKind.MyTasks,
		connectionId,
		didUserManuallyLoadMore || Boolean(searchTerm)
	);

	const isWide: boolean = dimensionToRender.kind === "normal"
		&& dimensionToRender.dimension
			.columnSpan > 4;

	const loadMoreHandler = useCallback(
		async (): Promise<void> => {
			if (loadingState.isLoading === true
				|| isNil(loadingState.response)
				|| !loadingState.response.hasMore
			) {
				return;
			}

			setDidUserManuallyLoadMore(true);

			const currentResponse: LazyLoadPageResult<TaskListItem> = loadingState.response;

			setLoadingState({
				isLoading: true,
				response: currentResponse
			});

			const skip: number = currentResponse
				.items
				.length;

			try {
				loadMoreAndSearchAbortControllerRef.current
					?.abort();

				const cancellation = new AbortController();
				loadMoreAndSearchAbortControllerRef.current = cancellation;

				const results: MyTasksListModel = await getMyTasks(
					connectionId,
					skip,
					PAGE_SIZE,
					searchTerm,
					loadMoreAndSearchAbortControllerRef.current
						.signal
				);

				switch (results.status) {
					case AfasResultStatus.Success:
						setLoadingState({
							isLoading: false,
							response: {
								hasMore: results.items.hasMore,
								items: [
									...currentResponse.items,
									...results.items.items
								]
							}
						});
						return;
					case AfasResultStatus.GeneralFailure:
						setLoadingState({
							isLoading: false,
							response: {
								...currentResponse,
								hasMore: false
							}
						});
						break;
					case AfasResultStatus.RequireNewToken:
					case AfasResultStatus.NoAccess:
						setLoadingState({
							isLoading: false,
							error: results.status
						});
						break;
					default:
						never(results);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				setLoadingState({
					isLoading: false,
					response: {
						...currentResponse,
						hasMore: false
					}
				});

				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			connectionId,
			loadingState,
			searchTerm,
			setLoadingState
		]
	);

	const searchHandler = useCallback(
		async (newSearchValue: string): Promise<void> => {
			if (!loadingState.isLoading && isNotNil(loadingState.error)) {
				return;
			}

			loadMoreAndSearchAbortControllerRef.current
				?.abort();

			setDidUserManuallyLoadMore(false);
			setSearchTerm(newSearchValue);

			setLoadingState({
				isLoading: true
			});

			try {
				const cancellation = new AbortController();
				loadMoreAndSearchAbortControllerRef.current = cancellation;

				const results: MyTasksListModel = await getMyTasks(
					connectionId,
					0,
					PAGE_SIZE,
					newSearchValue,
					loadMoreAndSearchAbortControllerRef.current
						.signal
				);

				switch (results.status) {
					case AfasResultStatus.Success:
						setLoadingState({
							isLoading: false,
							response: results.items
						});
						return;
					case AfasResultStatus.GeneralFailure:
						console.error("Unable to retrieve items");

						setLoadingState({
							isLoading: false,
							hasGlobalFailure: true
						});
						break;
					case AfasResultStatus.RequireNewToken:
					case AfasResultStatus.NoAccess:
						setLoadingState({
							isLoading: false,
							error: results.status
						});
						break;
					default:
						never(results);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				setLoadingState({
					isLoading: false,
					hasGlobalFailure: true
				});

				alertManager.showError(localize("_GeneralErrorMessage"));
			}

		},
		[
			alertManager,
			connectionId,
			loadingState,
			setLoadingState
		]
	);

	const scrollHandler = useCallback(
		(scrollTop: number): void => {
			if (!didUserManuallyLoadMore || searchTerm) {
				return;
			}

			if (scrollTop <= 0) {
				loadMoreAndSearchAbortControllerRef.current
					?.abort();

				setDidUserManuallyLoadMore(false);
			}
		},
		[
			didUserManuallyLoadMore,
			searchTerm
		]
	);

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading === false) {
			if (loadingState.hasTooManyConsecutiveErrors) {
				return <TileRefresh onRefresh={refreshHandler} />;
			}

			if (loadingState.hasGlobalFailure) {
				return (
					<LiveTileContent.Message
						text={localize("_UnableToRetrieveItems")}
					/>
				);
			}

			if (isNotNil(loadingState.error)) {
				return (
					<>
						<LiveTileContent.Message
							text={localize(
								loadingState.error === AfasResultStatus.RequireNewToken
									? "_AfasActivationIsRequiredMessage"
									: "_AfasNoAppAccessAndSuggestToActivateMessage"
							)}
						/>
						<div className={styles.activateButtonContainer}>
							<Button
								onClick={() => setIsOtpDialogOpen(true)}
								text={localize("_Activate")}
								kind="primary"
								color="app"
							/>
						</div>
					</>
				);
			}
		}

		if (
			loadingState.response
				?.items
				.length === 0
		) {
			return (
				<LiveTileContent.Message
					text={localize(
						searchTerm === ""
							? "_YouDontHaveAnyTasks"
							: "_NoResultsFound"
					)}
				/>
			);
		}

		return (
			<LiveTileContent.Scrollable
				onScroll={scrollHandler}
			>
				<LiveTileContent.List>
					{loadingState.response?.items
						.map((x) => (
							<MyTasksListItem
								key={x.id}
								item={x}
								destinationWindow={destinationWindow}
								isWide={isWide}
								timezone={timezone}
								onClick={onListItemClick}
							/>
						))
					}
				</LiveTileContent.List>
				{loadingState.response?.hasMore && (
					<LiveTileContent.LoadMoreButton onClick={loadMoreHandler}/>
				)}
			</LiveTileContent.Scrollable>
		);
	};

	useEffect(
		(): EffectWithDestructorResult => {
			return () => loadMoreAndSearchAbortControllerRef.current
				?.abort();
		},
		[]
	);

	return (
		<>
			{(loadingState.isLoading || isNil(loadingState.error)) && (
				<LiveTileContent.SearchInput
					onChange={searchHandler}
					trigger={{ kind: ChangeTriggerKind.Debounced }}
					placeholder={localize("_Search")}
				/>
			)}
			<LiveTileContent.BusyIndicatorContainer>
				{loadingState.isLoading && (
					<BusyIndicator />
				)}
				{renderContent()}
			</LiveTileContent.BusyIndicatorContainer>
			{isOtpDialogOpen && (
				<OtpDialog
					onClose={() => setIsOtpDialogOpen(false)}
					connectionId={connectionId}
					onAfterSave={refreshHandler}
				/>
			)}
		</>
	);
};
MyTasks.displayName="MyTasks";

type MyTasksListItemProps = {
	item: TaskListItem;
	destinationWindow: DestinationWindow;
	isWide: boolean;
	timezone: TimeZone;
	onClick(): void;
};

const MyTasksListItem = (props: MyTasksListItemProps): ReactElement => {
	const {
		destinationWindow,
		isWide,
		item,
		timezone,
		onClick
	} = props;

	const clickHandler = useCallback(
		(): void => {
			onClick();
			navigateTo(
				item.navigationUrl,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[
			destinationWindow,
			item.navigationUrl,
			onClick
		]
	);

	return (
		<ListItem
			onClick={clickHandler}
			isHoverable
		>
			<ListItem.Content>
				<ListItem.Row>
					<ListItem.Title>
						{item.title}
					</ListItem.Title>
				</ListItem.Row>
				<ListItem.Row>
					<ListItem.Info>
						{item.type}
						{isWide && item.action && ` | ${item.action}`}
					</ListItem.Info>
					<ListItem.RightAddition>
						<ListItem.Info>
							<DateString
								value={item.submitDate}
								format="AbsoluteTimestamp"
								timezone={timezone}
							/>
						</ListItem.Info>
					</ListItem.RightAddition>
				</ListItem.Row>
			</ListItem.Content>
		</ListItem>
	);
};

MyTasksListItem.displayName = "MyTasks.ListItem";